import { compose, connectWithHammock } from 'hammock';
import React from 'react';
import { ENDPOINTS } from 'site-constants';

import { Senstitives, withSensitives } from 'commons/sensitives';
import { BuiltInComponents, isTooltipContent, isTooltipDocument } from 'craft/constants';
import { ContentDocument } from 'craft/types';

import { DocumentScreenshot, DocumentScreenshotProps } from '../DocumentScreenshot';
import { CanduContentFrame } from '../components/CanduContentFrame';

interface DocumentPreviewProps {
  findRootNodeId?: (document: ContentDocument) => string | undefined;
  checkeredBackdrop?: boolean;
  showEmptyDocumentFallback?: boolean;
  stylesheetFromSource?: boolean;
  hotspotLimit?: number;
  onGetDocument?: (document: any) => any;
  isSdkPreview?: boolean;
}

const DocumentPreviewSDKImpl = React.memo(
  ({
    document,
    documents,
    findRootNodeId,
    clientToken,
    checkeredBackdrop,
    hotspotLimit,
    stylesheetFromSource,
    onGetDocument,
    isSdkPreview,
    showEmptyDocumentFallback,
  }: {
    document?: any;
    documents?: any[];
    clientToken: string;
  } & DocumentPreviewProps) => {
    const propsDocument = document || documents?.[0];

    const getDocumentToRender = () => {
      if (!findRootNodeId) {
        if (onGetDocument) {
          return onGetDocument(propsDocument);
        }
        return propsDocument;
      }

      const rootNodeId = findRootNodeId(propsDocument.document);
      if (!rootNodeId) {
        return null;
      }

      return {
        ...propsDocument,
        document: { ...propsDocument.document, rootNodeId },
      };
    };

    const documentToRender = getDocumentToRender();
    if (!documentToRender) {
      return null;
    }

    const rootNodeType =
      documentToRender.document?.nodes[documentToRender.document?.rootNodeId].type;

    return (
      <CanduContentFrame
        stylesheetFromSource={stylesheetFromSource}
        clientToken={clientToken}
        document={documentToRender}
        checkeredBackdrop={
          checkeredBackdrop ||
          isTooltipDocument(rootNodeType) ||
          rootNodeType === BuiltInComponents.Modal
        }
        showEmptyDocumentFallback={showEmptyDocumentFallback}
        hotspotLimit={hotspotLimit}
        isSdkPreview={isSdkPreview}
        nodeId={findRootNodeId ? findRootNodeId(propsDocument.document) : undefined}
      />
    );
  },
);

interface DocumentPreviewSdkProps extends DocumentPreviewProps {
  documentId?: number;
  versionId?: string;
  commitId?: string;
  isTemplate?: boolean;
}

const withDocument = connectWithHammock(
  ({ documentId, versionId, commitId, isTemplate }: DocumentPreviewSdkProps) => ({
    endpoint: isTemplate ? ENDPOINTS.templateDocumentsEditor : ENDPOINTS.documentsSdk,
    ...(documentId ? { id: documentId, resourceName: 'document' } : {}),

    ...(versionId && !documentId && !commitId
      ? {
          query: { finder: 'byTreeHash', treeHash: versionId, css: 'shake' },
          resourceName: 'documents',
        }
      : {}),

    ...(commitId && !documentId && !versionId
      ? { query: { finder: 'byCommitId', commitId, css: 'shake' }, resourceName: 'documents' }
      : {}),
  }),
);

const DocumentPreviewSDK = compose(withDocument)(DocumentPreviewSDKImpl);

const DocumentPreviewImpl = ({
  isSdkPreview,
  sensitives,
  overrideClientToken,
  checkeredBackdrop,
  ...rest
}: {
  isSdkPreview?: boolean;
  sensitives: Senstitives;
  overrideClientToken?: string;
  applicationId?: number;
} & (DocumentPreviewSdkProps | DocumentScreenshotProps)) => {
  if (
    !isSdkPreview &&
    'versionId' in rest &&
    'rootNodeType' in rest &&
    !isTooltipContent(rest.rootNodeType)
  ) {
    return (
      <DocumentScreenshot
        clientToken={overrideClientToken || sensitives[0].clientToken}
        checkeredBackdrop={
          checkeredBackdrop ||
          isTooltipDocument(rest.rootNodeType) ||
          rest.rootNodeType === BuiltInComponents.Modal
        }
        {...rest}
      />
    );
  }

  return (
    <DocumentPreviewSDK
      isSdkPreview={isSdkPreview}
      clientToken={overrideClientToken || sensitives[0].clientToken}
      checkeredBackdrop={checkeredBackdrop}
      {...rest}
    />
  );
};

export const DocumentPreview = compose(withSensitives)(DocumentPreviewImpl);
